import React, { useState } from 'react';
import UploadIcon from "../../../images/upload-icon.svg"
import './FileUploader.scss';

interface FileUploaderProps {
  onFileSelect?: (file: File | null) => void;
  onUploadSuccess?: (imageUrl: string) => void;
  onUploadError?: (error: string) => void;
  onFileUpload?: (imageUrl: string) => void; // For backward compatibility
  uploadEndpoint?: string;
  showPreview?: boolean;
  disabled?: boolean;
  initialImageUrl?: string;
}

const FileUploader: React.FC<FileUploaderProps> = ({
  onFileSelect,
  onUploadSuccess,
  onUploadError,
  onFileUpload, // For backward compatibility
  uploadEndpoint,
  showPreview = true,
  disabled = false,
  initialImageUrl
}) => {
  const [preview, setPreview] = useState<string | null>(initialImageUrl || null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  // Update preview when initialImageUrl changes
  React.useEffect(() => {
    if (initialImageUrl) {
      setPreview(initialImageUrl);
    }
  }, [initialImageUrl]);

  const validateFile = (file: File): boolean => {
    const allowedTypes = ['image/jpeg', 'image/png'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      const error = "Only JPG or PNG files are allowed.";
      alert(error);
      onUploadError?.(error);
      return false;
    }

    if (file.size > maxSize) {
      const error = "File size must be less than 5MB.";
      alert(error);
      onUploadError?.(error);
      return false;
    }

    return true;
  };

  const processFile = (file: File) => {
    if (!validateFile(file)) return;

    setSelectedFile(file);
    onFileSelect?.(file);

    if (showPreview) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }

    // Auto-upload if endpoint is provided
    if (uploadEndpoint) {
      uploadFile(file);
    }
  };

  const uploadFile = async (file: File) => {
    if (!uploadEndpoint) return;

    setUploading(true);
    
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch(uploadEndpoint, {
        method: 'POST',
        body: formData,
        // Add any required headers here (like authorization)
        // headers: {
        //   'Authorization': `Bearer ${token}`,
        // },
      });

      const result = await response.json();

      if (response.ok && result.status === 'success') {
        onUploadSuccess?.(result.data?.imageUrl || '');
        onFileUpload?.(result.data?.imageUrl || ''); // For backward compatibility
      } else {
        const errorMessage = result.message || 'Upload failed';
        onUploadError?.(errorMessage);
        alert(errorMessage);
      }
    } catch (error) {
      const errorMessage = 'Network error occurred during upload';
      console.error('Upload error:', error);
      onUploadError?.(errorMessage);
      alert(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabled || uploading) return;

    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const removeImage = () => {
    setPreview(null);
    setSelectedFile(null);
    onFileSelect?.(null);
    
    // Clear the file input
    const fileInput = document.getElementById('fileUpload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const triggerFileInput = () => {
    if (disabled || uploading) return;
    const fileInput = document.getElementById('fileUpload') as HTMLInputElement;
    fileInput?.click();
  };

  return (
    <div className="file-uploader">
      {/* Show upload box only when no preview is available */}
      {!(showPreview && preview) && (
        <div
          className={`upload-box ${disabled || uploading ? 'disabled' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={triggerFileInput}
          style={{ cursor: disabled || uploading ? 'not-allowed' : 'pointer' }}
        >
          <div className="upload-content">
            <div className="upload-icon">
              <img src={UploadIcon} alt="" />
            </div>
            <p>
              {uploading ? (
                'Uploading...'
              ) : (
                <>
                  Drag & Drop or{' '}
                  <span className='bold'>
                    Choose file{' '}
                  </span>
                  to upload
                </>
              )}
            </p>
            <span className="file-type">jpg, png</span>
            {uploading && (
              <div className="upload-progress">
                <div className="spinner"></div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Show preview if uploaded */}
      {showPreview && preview && (
        <div className="preview-container">
          <img src={preview} alt="Preview" className="preview-img" />
          <button
            className="close-btn"
            onClick={(e) => {
              e.stopPropagation();
              removeImage();
            }}
            disabled={uploading}
          >
            ×
          </button>
        </div>
      )}

      {/* File input always available */}
      <input
        id="fileUpload"
        type="file"
        accept="image/png, image/jpeg"
        onChange={handleFileChange}
        disabled={disabled || uploading}
        hidden
      />
    </div>
  );
};

export default FileUploader;